import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMjLogsApi, getSelfMjLogsApi, type MjLogEntry } from '@/api/midjourney'

const STORAGE_KEY = 'mj-logs.visible-columns.v1'

export type MjScope = 'self' | 'all'

export interface MjLogsFilterState {
  scope: MjScope
  mj_id: string
  channel_id?: number
  start_timestamp?: number
  end_timestamp?: number
  status?: string
}

export const MJ_COLUMN_KEYS = [
  'submit_time',
  'finish_time',
  'channel_id',
  'action',
  'mj_id',
  'prompt',
  'status',
  'progress',
  'image_url',
  'fail_reason',
] as const

export type MjColumnKey = typeof MJ_COLUMN_KEYS[number]

const DEFAULT_VISIBLE: MjColumnKey[] = [
  'submit_time',
  'action',
  'mj_id',
  'prompt',
  'status',
  'progress',
  'image_url',
]

function loadVisibleColumns(): MjColumnKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_VISIBLE
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return DEFAULT_VISIBLE
    return arr.filter((x): x is MjColumnKey => (MJ_COLUMN_KEYS as readonly string[]).includes(x))
  } catch {
    return DEFAULT_VISIBLE
  }
}

export function useMjLogs(isAdmin: boolean) {
  const [filters, setFilters] = useState<MjLogsFilterState>({
    scope: 'self',
    mj_id: '',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [visibleColumns, setVisibleColumnsState] =
    useState<MjColumnKey[]>(loadVisibleColumns)

  const setVisibleColumns = (cols: MjColumnKey[]) => {
    setVisibleColumnsState(cols)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cols))
    } catch {
      /* ignore quota errors */
    }
  }

  const query = useQuery({
    queryKey: ['mj-logs', filters, page, pageSize, isAdmin],
    queryFn: () => {
      const params = {
        p: page,
        page_size: pageSize,
        mj_id: filters.mj_id || undefined,
        channel_id: filters.channel_id,
        start_timestamp: filters.start_timestamp,
        end_timestamp: filters.end_timestamp,
      }
      if (filters.scope === 'self' || !isAdmin) return getSelfMjLogsApi(params)
      return getMjLogsApi(params)
    },
    placeholderData: (prev) => prev,
  })

  // 客户端再按 status 过滤（后端暂未暴露 status 参数）
  const filteredItems = useMemo<MjLogEntry[]>(() => {
    const raw = query.data?.items || []
    if (!filters.status) return raw
    return raw.filter((x) => (x.status || '').toUpperCase() === filters.status)
  }, [query.data, filters.status])

  const updateFilters = (patch: Partial<MjLogsFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const resetFilters = () => {
    setFilters({ scope: filters.scope, mj_id: '' })
    setPage(1)
  }

  return {
    filters,
    updateFilters,
    resetFilters,
    page,
    pageSize,
    setPage,
    setPageSize,
    query,
    items: filteredItems,
    total: query.data?.total ?? 0,
    visibleColumns,
    setVisibleColumns,
  }
}
